import React from 'react';
import { colors } from '../../styles/tokens';
import { useEsMovil } from '../../hooks/useEsMovil';
import type { TiendaPeriodo } from '../../types/indicadores.types';
import { fmtInt, fmtPct } from '../../utils/format';
import { GLOSARIO, semaforo, type MetricaConSemaforo } from '../../utils/glosario';
import { computeDelta, computeDeltaColor } from '../../utils/tienda';

export interface KpisHoyProps {
  hoy: TiendaPeriodo;
  ayer: TiendaPeriodo;
  /**
   * Literal del periodo de comparación mostrado en el delta: `'ayer'` en vivo;
   * con un periodo elegido, `'anterior'`, porque el bloque `ayer` de la
   * respuesta es entonces el periodo anterior de igual longitud.
   */
  comparativo?: string;
}

interface KpiDef {
  key: string;
  label: string;
  /** Color de la barra de acento: fijo por métrica o el de su semáforo. */
  accent: string;
  value: string;
  /**
   * Color de la cifra: el del semáforo de la métrica, si tiene y hay dato; si
   * no, el de texto. Contrato común (SPEC 2026-10-12, «Semáforo, deltas y
   * ayudas»): el semáforo pinta la barra Y la cifra, como el medidor y la tabla
   * de Global y el `KpisHoy` de la app.
   */
  valueColor?: string;
  /** Fracción (no 0..100) o `null` cuando no hay delta que mostrar. */
  delta: number | null;
  /** Invierte el color del delta (subir = rojo): sólo Negados. */
  invertDeltaColor?: boolean;
  /**
   * Delta en gris, sin juicio: SÓLO la Tasa de atención. No es monótona —de
   * 60 % a 90 % mejora, de 150 % a 210 % empeora—, así que ni el verde de
   * «subió» ni el invertido dicen la verdad; el juicio lo da su semáforo, en la
   * barra y la cifra.
   */
  deltaNeutro?: boolean;
  /** Ayuda corta del glosario (ícono «i»). */
  note?: string;
}

/**
 * Color de la cifra de una métrica con semáforo: el del semáforo cuando hay
 * dato y, sin dato, `undefined` (la tarjeta cae al color de texto). El gris de
 * «sin dato» de `semaforo` es para la barra; en la cifra, un «—» gris se leería
 * como un dato atenuado. Mismo criterio que la app.
 */
function colorCifra(metrica: MetricaConSemaforo, fraccion: number | null | undefined): string | undefined {
  return fraccion === null || fraccion === undefined || Number.isNaN(fraccion) ? undefined : semaforo(metrica, fraccion);
}

/**
 * La banda OPERATIVA de la vista Tienda: siete tarjetas «hoy vs ayer» (o
 * «periodo vs anterior») con los rótulos del glosario —los mismos del embudo y
 * los medidores de Global—: Consultas · Solicitudes a bodega · Solicitudes
 * atendidas · Negados · Tasa de atención · Atendidas por bodeguero ·
 * Efectividad de inventario.
 *
 * El delta es REAL: `(hoy.x - ayer.x) / ayer.x` (`computeDelta`, null-safe).
 * Efectividad de inventario nunca lleva delta (línea 416 del handoff).
 *
 * ⚠️ La venta se fue de aquí el 12-oct: las tarjetas de dinero son ahora las
 * MISMAS de Global (`VentaCards`), Descuento y Préstamos incluidos.
 *
 * ⚠️ «Tasa de atención» = V ÷ A (la API cambió `pctTasa` de V ÷ S a V ÷ A el
 * 12-oct): la misma fórmula, el mismo rótulo y el mismo semáforo que Global.
 * VAZ 56 en la S40 marcaba 46,6 % aquí y 49,5 % en Global.
 *
 * Semáforo en la barra Y en la cifra (tasa y A ÷ S); sin dato, la cifra va en
 * el color de texto. El delta de la tasa va en gris (`deltaNeutro`), el de
 * Negados invertido y los demás normal. Revisión cruzada del 12-oct: una tienda
 * a 212 % salía con la cifra en negro aquí y en rojo en la app; ahora las dos
 * superficies pintan igual (SPEC, «Semáforo, deltas y ayudas»).
 */
/**
 * Por debajo de este ancho (y por encima del corte de teléfono) «Atendidas por
 * bodeguero» envuelve en TRES líneas. No es un número elegido: medido en Chrome
 * con las siete tarjetas, a 1160 px son 3 líneas y desde 1200 px, 2.
 */
export const CORTE_ETIQUETA_TRES_LINEAS = 1200;

const KpisHoy: React.FC<KpisHoyProps> = ({ hoy, ayer, comparativo = 'ayer' }) => {
  const esMovil = useEsMovil();
  const angosta = useEsMovil(CORTE_ETIQUETA_TRES_LINEAS - 1);
  const etiquetaDeTres = !esMovil && angosta;

  const kpis: KpiDef[] = [
    {
      key: 'consultas',
      label: GLOSARIO.consultas.rotulo,
      accent: colors.naranja,
      value: fmtInt(hoy.consultas),
      delta: computeDelta(hoy.consultas, ayer.consultas),
      note: GLOSARIO.consultas.nota,
    },
    {
      key: 'solicitudes',
      label: GLOSARIO.solicitudes.rotulo,
      accent: colors.brand,
      value: fmtInt(hoy.solicitudes),
      delta: computeDelta(hoy.solicitudes, ayer.solicitudes),
    },
    {
      key: 'atendidas',
      label: GLOSARIO.atendidas.rotulo,
      accent: colors.verde,
      value: fmtInt(hoy.atendidas),
      delta: computeDelta(hoy.atendidas, ayer.atendidas),
    },
    {
      key: 'negados',
      label: GLOSARIO.negados.rotulo,
      accent: colors.rojo,
      value: fmtInt(hoy.negados),
      delta: computeDelta(hoy.negados, ayer.negados),
      invertDeltaColor: true,
    },
    {
      key: 'tasa',
      label: GLOSARIO.tasa.rotulo,
      // Semáforo de Global: la barra y la cifra dicen lo mismo que el medidor.
      accent: semaforo('tasa', hoy.pctTasa),
      valueColor: colorCifra('tasa', hoy.pctTasa),
      value: fmtPct(hoy.pctTasa),
      delta: computeDelta(hoy.pctTasa, ayer.pctTasa),
      deltaNeutro: true,
      note: GLOSARIO.tasa.nota,
    },
    {
      key: 'sla',
      label: GLOSARIO.atendidasBodega.rotulo,
      accent: semaforo('atendidasBodega', hoy.pctSla),
      valueColor: colorCifra('atendidasBodega', hoy.pctSla),
      value: fmtPct(hoy.pctSla),
      delta: computeDelta(hoy.pctSla, ayer.pctSla),
      note: `${GLOSARIO.atendidasBodega.nota} (${GLOSARIO.atendidasBodega.formula})`,
    },
    {
      // Sin semáforo: barra de marca y cifra en el color de texto.
      key: 'efectividad',
      label: GLOSARIO.efectividad.rotulo,
      accent: semaforo('efectividad', hoy.pctEfectividad),
      value: fmtPct(hoy.pctEfectividad),
      delta: null,
      note: `${GLOSARIO.efectividad.nota} (${GLOSARIO.efectividad.formula})`,
    },
  ];

  return (
    <div
      data-testid="kpis-hoy"
      style={{
        display: 'grid',
        // En teléfono 2 columnas fijas (cifras cortas). En escritorio SIETE
        // columnas, no `auto-fit`: con `auto-fit` la última fila queda a medias
        // en cuanto el número de tarjetas no divide el ancho (lección «la
        // rejilla que nunca cuadraba»). Siete tarjetas, siete columnas, cero
        // huecos a cualquier ancho.
        gridTemplateColumns: esMovil ? 'repeat(2, minmax(0, 1fr))' : 'repeat(7, minmax(0, 1fr))',
        gap: 12,
        marginTop: esMovil ? 12 : 14,
      }}
    >
      {kpis.map((k, i) => {
        // En teléfono son 2 columnas y 7 tarjetas: la última ocupa la fila
        // entera en vez de dejar media fila vacía (mismo criterio que la app).
        const ultimaSola = esMovil && i === kpis.length - 1 && kpis.length % 2 === 1;
        const deltaColor = k.deltaNeutro ? colors.texto.quaternary : computeDeltaColor(k.delta, k.invertDeltaColor);
        const deltaTxt =
          k.delta === null
            ? ''
            : `${k.delta >= 0 ? '▲' : '▼'} ${fmtPct(Math.abs(k.delta))} vs ${comparativo}`;

        return (
          <div
            key={k.key}
            data-testid={`kpi-${k.key}`}
            style={{
              gridColumn: ultimaSola ? '1 / -1' : undefined,
              // Sólo en móvil: en escritorio las columnas son las que mandan.
              minWidth: esMovil ? 0 : undefined,
              background: colors.card,
              border: `1px solid ${colors.border}`,
              borderRadius: 14,
              padding: esMovil ? '13px 14px' : '14px 16px',
              boxShadow: '0 1px 2px rgba(20,28,54,.05)',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: k.accent }} />
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 4,
                // DOS LÍNEAS RESERVADAS, también en teléfono: «Efectividad de
                // inventario» y «Atendidas por bodeguero» envuelven, y sin altura
                // reservada su cifra caía más abajo que la de las vecinas. El
                // interlineado va fijo porque `2.4em` con el `normal` de Manrope
                // no alcanzaba para dos líneas: medido en Chrome, la cifra quedaba
                // 7 px más abajo.
                // Entre el corte de teléfono y 1200 px se reservan TRES.
                lineHeight: 1.25,
                minHeight: etiquetaDeTres ? '3.75em' : '2.5em',
                // Piso de 11 px para etiquetas en teléfono.
                fontSize: esMovil ? 11 : 10.5,
                letterSpacing: '.05em',
                textTransform: 'uppercase',
                color: colors.texto.quaternary,
                fontWeight: 700,
              }}
            >
              {k.label}
              {k.note && (
                <span
                  title={k.note}
                  aria-label={k.note}
                  data-testid={`${k.key}-note`}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                    width: esMovil ? 16 : 13,
                    height: esMovil ? 16 : 13,
                    borderRadius: '50%',
                    background: colors.ringTrack,
                    color: colors.texto.tertiary,
                    fontSize: esMovil ? 11 : 9.5,
                    fontWeight: 800,
                    textTransform: 'none',
                    letterSpacing: 0,
                    cursor: 'help',
                  }}
                >
                  i
                </span>
              )}
            </div>
            <div
              data-testid={`kpi-${k.key}-valor`}
              style={{
                fontSize: 26,
                fontWeight: 800,
                color: k.valueColor ?? colors.texto.primary,
                fontVariantNumeric: 'tabular-nums',
                marginTop: 4,
                letterSpacing: '-.01em',
              }}
            >
              {k.value}
            </div>
            <div
              style={{
                fontSize: esMovil ? 12 : 11.5,
                fontWeight: 700,
                marginTop: 2,
                color: deltaColor,
                minHeight: '1.2em',
              }}
            >
              {deltaTxt}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default KpisHoy;
